import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { JOB_CARD } from '../lib/card-fields.ts';
import { RecordList } from '../components/boards/record-list.tsx';
import {
  AddToPipelineDialog,
  BulkPipelineBar,
  describeAddResult,
} from '../components/pipelines/add-to-pipeline-bulk.tsx';
import { PipelinePicker } from '../components/pipelines/pipeline-picker.tsx';
import { StagePill } from '../components/ui/tone-pill.tsx';
import { type ColumnDef } from '../components/record-table/index.ts';
import { Gate } from '../components/shell/gate.tsx';
import { ToggleButton } from '../components/ui/toggle-button.tsx';
import { api, type JobView, type PipelineView } from '../lib/client.ts';
import { joinJobFilter, myOpenJobs, splitJobFilter } from '../lib/job-filters.ts';
import { useVisibleDefinitions } from '../lib/custom-fields.ts';
import { jobFilterFields } from '../lib/list-filters.ts';
import { useListView } from '../lib/list-view.ts';
import { pickedPipeline, placeList, stageInPlace } from '../lib/place-lists.ts';
import { useMembers, usePipelines } from '../lib/record-data.ts';
import { useSession } from '../lib/session.tsx';
import { PageHeader } from '../components/ui/page-header.tsx';

/** Jobs in one Job Flow pipeline: search, totals, and a move through `:move-status`. */
const jobList = (pipelineId: string | undefined) =>
  placeList<JobView>(pipelineId, {
    search: (body) => api.jobs.search(body),
    counts: (body) => api.jobs.counts(body),
    move: (id, stageId) => api.jobs.moveStatus(id, stageId),
    titleOf: (j) => j.title,
    sortable: [
      'title',
      'interviewDateTime',
      'openDate',
      'statusChangedAt',
      'createdAt',
      'updatedAt',
    ],
  });

/**
 * Jobs across every project (RELAY-953, RELAY-1038). Recruiters land on "my open jobs": the
 * jobs they are recruiter, coordinator or account manager on, still in an open Job Status,
 * without opening a single project. The same list as a table or a board by Job Status, and
 * opening a job goes to its project, scrolled to that job.
 *
 * Like Projects, the list shows ONE Job Flow pipeline, picked beside the toolbar: the default lists
 * every job by its Job Status; an extra one only the jobs in it, at their place there ("Open only"
 * then means not won or lost THERE). Ticked rows can be added to an extra pipeline.
 */
export function JobsPage() {
  const pipelines = usePipelines('job');
  const pipeline = pipelines.find((p) => p.isDefault);
  return (
    <Gate permission="job.read" feature="jobs.enabled">
      {/* "Open only" is read from the pipeline: nothing is asked for until it has loaded, so a
          closed job never flashes into "my open jobs". */}
      {pipeline ? (
        <JobsList pipelines={pipelines} defaultPipeline={pipeline} />
      ) : (
        <p className="text-muted-foreground text-sm">Loading…</p>
      )}
    </Gate>
  );
}

function JobsList({
  pipelines,
  defaultPipeline,
}: {
  pipelines: readonly PipelineView[];
  defaultPipeline: PipelineView;
}) {
  const session = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const search = useSearch({ from: '/authed/jobs' });
  const { members, nameOf } = useMembers();
  const definitions = useVisibleDefinitions('jobs');
  const userId = session.me.user.id;
  const write = useCallback(
    (view: string) => void navigate({ to: '/jobs', search: { view }, replace: true }),
    [navigate],
  );
  const initial = useMemo(() => myOpenJobs(userId, defaultPipeline), [userId, defaultPipeline]);
  const { state, setState, activeViewId } = useListView({
    entity: 'jobs',
    remember: 'jobs',
    encoded: search.view,
    write,
    initial,
  });
  const pipeline = pickedPipeline(pipelines, state.pipelineId) ?? defaultPipeline;
  const pipelineId = pipeline.isDefault ? undefined : pipeline.id;
  const listApi = useMemo(() => jobList(pipelineId), [pipelineId]);
  const quick = useMemo(
    () => splitJobFilter(state.filter, userId, pipeline),
    [state.filter, userId, pipeline],
  );
  const setQuick = (next: Partial<typeof quick>) =>
    setState({ ...state, filter: joinJobFilter({ ...quick, ...next }, userId, pipeline) });
  // Another pipeline: a stage filter named the old one's stages and goes; "Open only" stays on,
  // read from the new pipeline's won and lost stages.
  const pickPipeline = (id: string) => {
    const next = pipelines.find((p) => p.id === id) ?? defaultPipeline;
    setState({
      ...state,
      pipelineId: next.isDefault ? undefined : next.id,
      filter: joinJobFilter(
        { ...quick, conditions: quick.conditions.filter((c) => c.field !== 'stageId') },
        userId,
        next,
      ),
    });
  };

  // Bulk: tick rows in the table, then add them to an extra pipeline.
  const canUpdate = session.can('job.update');
  const extras = useMemo(() => pipelines.filter((p) => !p.isDefault), [pipelines]);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [adding, setAdding] = useState<string[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const tableLayout = !state.layout || state.layout === 'table';
  const selectionScope = JSON.stringify([
    state.filter ?? null,
    state.q ?? null,
    pipelineId,
    state.layout,
  ]);
  useEffect(() => setSelected(new Set()), [selectionScope]);

  const filterFields = useMemo(
    () => jobFilterFields({ definitions, search: listApi.search, pipeline, members }),
    [definitions, listApi, pipeline, members],
  );
  const columns = useMemo<ColumnDef<JobView, unknown>[]>(
    () => [
      { accessorKey: 'title', header: 'Job', size: 240 },
      {
        id: 'project',
        header: 'Project',
        size: 240,
        enableSorting: false,
        cell: ({ row }) => `${row.original.project.name} · ${row.original.project.companyName}`,
      },
      {
        id: 'status',
        header: pipeline.name,
        size: 200,
        // Measured by the stage's name; the pill adds its padding.
        accessorFn: (j) => stageInPlace(j, pipelineId)?.name ?? '',
        meta: { autoSize: 24 },
        enableSorting: false,
        cell: ({ row }) => {
          const stage = stageInPlace(row.original, pipelineId);
          if (!stage) return <span className="text-muted-foreground">—</span>;
          return <StagePill name={stage.name} terminalKind={stage.terminalKind} />;
        },
      },
      {
        id: 'recruiter',
        header: 'Recruiter',
        size: 160,
        enableSorting: false,
        cell: ({ row }) => nameOf(row.original.recruiterId),
      },
      {
        id: 'interviewDateTime',
        accessorKey: 'interviewDateTime',
        header: 'Interview',
        size: 150,
        cell: ({ row }) =>
          row.original.interviewDateTime
            ? new Date(row.original.interviewDateTime).toLocaleString()
            : '—',
      },
      {
        id: 'openings',
        header: 'Filled',
        size: 90,
        enableSorting: false,
        cell: ({ row }) => `${row.original.filled} / ${row.original.openings}`,
      },
    ],
    [nameOf, pipeline, pipelineId],
  );

  return (
    <>
      <PageHeader
        title="Jobs"
        description={
          <>
            {quick.mine ? 'Jobs you are on' : 'Every job'}
            {quick.openOnly ? ', still open' : ''}, across all projects.
          </>
        }
        actions={
          notice && (
            <span className="text-muted-foreground text-sm" role="status">
              {notice}
            </span>
          )
        }
      />
      {canUpdate && extras.length > 0 && tableLayout && selected.size > 0 && (
        <div className="mb-3">
          <BulkPipelineBar
            count={selected.size}
            onAdd={() => setAdding([...selected])}
            onClear={() => setSelected(new Set())}
          />
        </div>
      )}
      <RecordList
        key={pipeline.id}
        entity="jobs"
        state={state}
        setState={setState}
        activeViewId={activeViewId}
        filter={state.filter}
        conditions={quick.conditions}
        onConditionsChange={(conditions) => setQuick({ conditions })}
        filterFields={filterFields}
        quickFilters={
          <>
            {pipelines.length > 1 && (
              <PipelinePicker pipelines={pipelines} value={pipeline.id} onChange={pickPipeline} />
            )}
            <ToggleButton
              pressed={quick.mine}
              onClick={() => setQuick({ mine: !quick.mine })}
              label="Mine"
            />
            <ToggleButton
              pressed={quick.openOnly}
              onClick={() => setQuick({ openOnly: !quick.openOnly })}
              label="Open only"
            />
          </>
        }
        api={listApi}
        columns={columns}
        pipeline={pipeline}
        cardSet={JOB_CARD}
        canMove={canUpdate}
        hideTerminal={quick.openOnly}
        onOpen={(job) =>
          void navigate({
            to: '/projects/$id',
            params: { id: job.projectId },
            search: { job: job.id },
          })
        }
        emptyMessage={quick.mine ? 'You are not on any open jobs.' : 'No jobs.'}
        searchPlaceholder="Search job titles or companies…"
        {...(canUpdate && extras.length > 0
          ? { selection: selected, onSelectionChange: setSelected }
          : {})}
      />
      {adding !== null && (
        <AddToPipelineDialog
          noun="job"
          ids={adding}
          pipelines={extras}
          current={pipeline.id}
          add={(body) => api.jobs.addManyToPipeline(body)}
          onClose={() => setAdding(null)}
          onDone={async (result) => {
            setAdding(null);
            setSelected(new Set());
            setNotice(describeAddResult(result));
            await Promise.all(
              [['records'], ['board', 'jobs'], ['project']].map((queryKey) =>
                qc.invalidateQueries({ queryKey }),
              ),
            );
          }}
        />
      )}
    </>
  );
}
